import React, { useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { MDSImage, MDSImageProps } from '@marqvision/mds-v2';
import { BoundingBox, LogoDetectionData } from './BoundingBox';

const useStyles = makeStyles(() => ({
  boundingBoxWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    '&.center': {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    },
  },
}));

type Props = {
  url: string;
  logoDetectionData?: LogoDetectionData;
  aspectRatio?: string;
  minHeight?: number;
  maxHeight?: number;
  hoverButton?: React.ReactElement;
} & Omit<
  MDSImageProps,
  'src' | 'aspectRatio' | 'minHeight' | 'maxHeight' | 'objectFit' | 'objectPosition' | 'onLoad' | 'errorFallback'
>;

// min-height, max-height 를 지정해 max-height 이상은 잘린 이미지 및 bounding box 출력
export const ResponsiveImage = (props: Props) => {
  const { url, logoDetectionData, minHeight, maxHeight, hoverButton, ...restProps } = props;
  const classes = useStyles();

  const [displaySize, setDisplaySize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [scaledSize, setScaledSize] = useState<{ width: number; height: number; aspectRatio?: number }>({
    width: 0,
    height: 0,
  });

  const isAboveHeight = maxHeight ? displaySize.height > maxHeight : scaledSize.height > displaySize.height;
  const objectPosition = isAboveHeight ? 'top center' : 'center';
  const objectFit = isAboveHeight ? 'cover' : 'contain';

  const handleImageLoaded = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const displayWidth = event.currentTarget.clientWidth;
    const naturalSize =
      logoDetectionData?.image_width && logoDetectionData?.image_height
        ? {
            width: logoDetectionData.image_width,
            height: logoDetectionData.image_height,
          }
        : { width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight };
    const naturalSizeAspectRatio = naturalSize.width / naturalSize.height;
    const displayAspectRatio = props.aspectRatio ? getAspectRatio(props.aspectRatio) : naturalSizeAspectRatio;

    setDisplaySize({ width: displayWidth, height: displayWidth / displayAspectRatio });
    setScaledSize({
      width: displayWidth,
      height: displayWidth / naturalSizeAspectRatio,
      aspectRatio: naturalSizeAspectRatio,
    });
  };

  return (
    <MDSImage
      src={url}
      width="100%"
      minHeight={minHeight ? `${minHeight}px` : undefined}
      maxHeight={maxHeight ? `${maxHeight}px` : undefined}
      aspectRatio={props.aspectRatio}
      objectFit={objectFit}
      objectPosition={objectPosition}
      custom={{ type: 'hover', element: hoverButton }}
      onLoad={handleImageLoaded}
      {...restProps}
    >
      {scaledSize.aspectRatio && logoDetectionData?.image_width && logoDetectionData.image_height && (
        // 이미지가 실제 비율로 출력되지 않기 때문에 실제 비율의 wrapper 를 만들어서 그 안에 bounding box 를 그림
        <div
          className={clsx(classes.boundingBoxWrapper, { center: objectPosition === 'center' })}
          style={{ width: '100%', aspectRatio: scaledSize.aspectRatio }}
        >
          <BoundingBox detectionData={logoDetectionData} />
        </div>
      )}
    </MDSImage>
  );
};

const getAspectRatio = (aspectRatio: string): number => {
  const [widthRatio, heightRatio] = aspectRatio.split('/').map(Number); // '4/3' -> [4, 3]
  return widthRatio / heightRatio;
};
